/**
 * Town-boss combat flow (HEX-122 / ADR-0018 §4). The town analogue of
 * `EncounterFlow`: it owns the combat-screen lifecycle for the fight that gates
 * a town's liberation.
 *
 * Differences from `EncounterFlow`:
 *   - the enemy is a scaled generic boss (`buildBossEncounter`), not a persisted
 *     encounter-tile template — towns carry no `enemyId`;
 *   - victory routes through `resolveBossOutcome`, which liberates the town and
 *     materialises its reserved pillar slot rather than marking a tile resolved;
 *   - defeat reuses the shared archive path (death stamp + town `fallenCount`).
 *
 * Outcome routing mirrors `EncounterFlow`: victory → close bar → liberate;
 * defeat → death screen → archive; forfeit (Esc) → retreat keeping combat HP,
 * town untouched.
 */

import type { Character } from '../core/character';
import type { Tile } from '../core/tile';
import {
  buildBossEncounter,
  buildBossTemplate,
  resolveBossOutcome,
} from '../lifecycle/bossEncounter';
import type { ResolveBossDeps } from '../lifecycle/bossEncounter';
import { type CardCombatState, applyIntent, createCombat } from './cardEngine';
import { CombatRewards } from './combatRewards';
import { combatSeedFor } from './encounter';
import type { CombatDrops, EnemyTier } from './types';
import {
  CardCombatOverlay,
  type EngineDriver,
  type CombatOutcome as OverlayOutcome,
} from './ui/CardCombatOverlay';
import { DeathScreen } from './ui/DeathScreen';

export type BossOutcome = OverlayOutcome;

export interface BossFlowStartOptions {
  readonly character: Character;
  /** The `town` tile being entered. */
  readonly townTile: Tile;
  /** Reserved `ground` tile at `townTile.lifecycleState.anchorTiles.pillar`. */
  readonly pillarSlotTile: Tile;
  readonly worldSeed: number;
  readonly deps: ResolveBossDeps;
  /**
   * Extra boss-tier offset from accumulated evil (HEX-123 / ADR-0018 §5).
   * Floored value from `accumulatedEvil()` in evilPressure.ts. Defaults to 0
   * (first liberation or no context).
   */
  readonly extraTierOffset?: number;
  /** Fires after the outcome settles. Defeat: host exits to roster. */
  readonly onResolved: (outcome: BossOutcome) => void | Promise<void>;
}

export class BossFlow {
  private overlay: CardCombatOverlay | null = null;
  private state: CardCombatState | null = null;
  private closeBar: HTMLDivElement | null = null;
  private readonly rewards: CombatRewards;
  private deathScreen: DeathScreen | null = null;
  private enemyTier: EnemyTier = 'common';
  private active = false;

  constructor(private readonly container: HTMLElement) {
    this.rewards = new CombatRewards(container);
  }

  /**
   * Spin up the boss fight if `townTile` is an evil town. Returns true if combat
   * started (overlay mounted, input should pause); false otherwise. The CALLER
   * decides whether the town is evil (via `getTownState`) — this only guards the
   * tile type and the active flag, mirroring `EncounterFlow.start`.
   */
  start(opts: BossFlowStartOptions): boolean {
    if (this.active) return false;
    if (opts.townTile.type !== 'town') return false;

    const extraTierOffset = opts.extraTierOffset ?? 0;
    const template = buildBossTemplate(opts.townTile, extraTierOffset);
    const drops = template.drops;
    this.enemyTier = template.tier;
    const encounter = buildBossEncounter(
      opts.character,
      opts.townTile,
      opts.worldSeed,
      extraTierOffset,
    );
    this.active = true;

    const overlay = new CardCombatOverlay();
    const driver: EngineDriver = {
      createCombat: (args) => {
        const next = createCombat(args);
        this.state = next;
        return next;
      },
      applyIntent: (state, intent) => {
        const next = applyIntent(state, intent);
        this.state = next;
        return next;
      },
    };
    overlay.open(this.container, encounter, driver, (outcome) => {
      if (outcome === 'forfeit') {
        void this.forfeit(opts);
        return;
      }
      if (outcome === 'defeat') {
        this.renderDeathScreen(opts, drops);
        return;
      }
      this.renderCloseBar(opts, outcome, drops);
    });
    this.overlay = overlay;
    return true;
  }

  isActive(): boolean {
    return this.active;
  }

  /** Test seam — current engine state, or null when inactive. */
  getState(): CardCombatState | null {
    return this.state;
  }

  destroy(): void {
    this.teardown();
  }

  private renderCloseBar(
    opts: BossFlowStartOptions,
    outcome: 'victory' | 'defeat',
    drops: CombatDrops,
  ): void {
    if (this.closeBar !== null) return;
    const bar = document.createElement('div');
    bar.dataset.role = 'combat-close-bar';
    bar.style.cssText =
      'position:absolute;bottom:18px;left:50%;transform:translateX(-50%);z-index:40;display:flex;gap:8px;';
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.action = 'close-combat';
    button.textContent = outcome === 'victory' ? 'Town liberated' : 'Return to roster';
    button.style.cssText =
      'padding:10px 14px;font-size:14px;background:#222230;color:inherit;border:1px solid #34344a;border-radius:6px;cursor:pointer;';
    button.addEventListener('click', () => {
      void this.close(opts, drops);
    });
    bar.appendChild(button);
    this.container.appendChild(bar);
    this.closeBar = bar;
  }

  private renderDeathScreen(opts: BossFlowStartOptions, drops: CombatDrops): void {
    if (this.deathScreen !== null) return;
    const state = this.state;
    const cause = state?.enemy.name ?? null;
    const hp = Math.max(0, state?.player.hp ?? 0);
    const screen = new DeathScreen(this.container);
    screen.start(
      {
        name: opts.character.name,
        discoveries: opts.character.discoveries,
        gold: opts.character.gold,
        hp,
        hex: { q: opts.townTile.q, r: opts.townTile.r },
        cause,
        inventory: opts.character.inventory,
      },
      () => {
        void this.close(opts, drops);
      },
    );
    this.deathScreen = screen;
  }

  private async close(opts: BossFlowStartOptions, drops: CombatDrops): Promise<void> {
    const state = this.state;
    if (!state || state.phase !== 'ended') return;
    const tier = this.enemyTier;
    this.teardown();
    const outcome = await resolveBossOutcome(
      state,
      opts.character,
      opts.townTile,
      opts.pillarSlotTile,
      opts.deps,
      drops,
    );
    if (outcome === 'victory') {
      // Reward parity with wild encounters (HEX-126): combat XP (HEX-117) +
      // tier-weighted card drop + level-up overlay. resolveBossOutcome already
      // saved (HP regen + gold); grantVictoryRewards saves again with the
      // updated xp/level so the run stays consistent.
      await this.rewards.grantVictoryRewards({
        character: opts.character,
        tier,
        seed: combatSeedFor(opts.character, opts.townTile, opts.worldSeed),
        deps: opts.deps,
      });
    }
    await opts.onResolved(outcome);
  }

  private async forfeit(opts: BossFlowStartOptions): Promise<void> {
    // Retreat keeps the combat damage (HEX-95 / ADR-0015 §19): carry the final
    // HP back and persist it. The town is NOT liberated — re-entry starts a fresh
    // fight.
    const state = this.state;
    if (state !== null) {
      opts.character.hp = Math.max(0, state.player.hp);
      await opts.deps.persistence.saveCharacter(opts.character);
    }
    this.teardown();
    await opts.onResolved('forfeit');
  }

  private teardown(): void {
    this.overlay?.destroy();
    this.overlay = null;
    this.closeBar?.remove();
    this.closeBar = null;
    this.deathScreen?.destroy();
    this.deathScreen = null;
    this.rewards.destroy();
    this.state = null;
    this.active = false;
  }
}
